import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import {connect} from 'react-redux';
import {IFamilyMember} from '@app-types/websocket/family.types';
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {FamilyMemberType} from "@services/family/family.constants";
import {EcoRepeat, IEcoSplit, IEcoSplitSetting} from "@app-types/websocket/economy.types";
import * as FileActions from '@services/file/file.actions';
import CordovaContext from '../../../cordovaContext';
import {
  Col,
  DialogTop,
  dialogStyles,
} from '../DialogComponens';
import StepOneCost from './StepOneCost';
import StepSubs from './StepSubs';
import StepInfo from './StepInfo';
import StepAddTitle from './StepAddTitle';

const tn = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof dialogStyles>{
  isOpen: boolean;
  onClose: () => void;

  settings:IEcoSplitSetting[];
  family: IFamilyMember[];
  currency:string;
}

interface IState {
  step:number;
  steps:JSX.Element[];
}


class AgreementSetupDialog extends React.Component<IProps, IState> {

  constructor(props: IProps) {
    super(props);

    this.state = {
      step:0,
      steps:this.createSteps(props),
    };
    
  }

  public createSteps(props:IProps) {
    const steps:JSX.Element[] = [];
    let index = 0;
    let kids = 0;

    steps.push(<StepInfo 
      key={index}
      text={tn.lang.agreGuide}
      next={() => this.step()}
    />); 
    index++;

    const split:IEcoSplit[] = (this.props.settings.length > 0)
    ?(this.props.settings[this.props.settings.length -1].split.map((sp) => ({accountId:sp.accountId, percentage:sp.percentage})))
    :this.props.family.filter(m => m.role === FamilyMemberType.PARENT)
    .map(m => ({
      accountId: m.id,
      percentage: 50,
    }));

    for (const mem of props.family) {

      if (mem.role === FamilyMemberType.CHILD) {
        const now = new Date();
        const age:number|undefined = mem.yearBorn?(now.getFullYear() - mem.yearBorn):undefined;
        kids++;

        steps.push(<StepOneCost 
          key={index}
          text={tn.lang.agreGuideInsurance(mem.firstName)}
          next={() => this.step()}
          desc={tn.lang.agreGuideInsuranceDesc(mem.firstName.charAt(0))}
          repeat={EcoRepeat.MONTHLY}
          repeatEditable={true}
          currency={this.props.currency}
          childId={mem.id}
          split={split}
        />); 
        index++;

        if (age === undefined || age > 4) {
          steps.push(<StepOneCost 
            key={index}
            text={tn.lang.agreGuidePocketmoney(mem.firstName)}
            next={() => this.step()}
            desc={tn.lang.agreGuidePocketmoneyDesc(mem.firstName.charAt(0))}
            repeat={EcoRepeat.WEEKLY}
            repeatEditable={true}
            currency={this.props.currency}
            childId={mem.id}
            split={split}  
          />);  
          index++;
        }

        if (age === undefined || age > 6) {
          steps.push(<StepOneCost 
            key={index}
            text={tn.lang.agreGuidePhone(mem.firstName)}
            next={() => this.step()}
            desc={tn.lang.agreGuidePhoneDesc(mem.firstName.charAt(0))}
            repeat={EcoRepeat.MONTHLY}
            repeatEditable={false}
            currency={this.props.currency}
            childId={mem.id}
            split={split}  
          />);
          index++;
        }

        if (age === undefined || age < 7) {
          steps.push(<StepOneCost 
            key={index}
            text={tn.lang.agreGuideKindergarten(mem.firstName)}
            next={() => this.step()}
            desc={tn.lang.agreGuideKindergartenDesc(mem.firstName.charAt(0))}
            repeat={EcoRepeat.MONTHLY}
            repeatEditable={false}
            currency={this.props.currency}
            childId={mem.id}
            split={split}
          />);
          index++; 
        } else if (age < 15) {
          steps.push(<StepOneCost 
            key={index}
            text={tn.lang.agreGuideSchool(mem.firstName)}
            next={() => this.step()}
            desc={tn.lang.agreGuideSchoolDesc(mem.firstName.charAt(0))}
            repeat={EcoRepeat.MONTHLY}
            repeatEditable={false}
            currency={this.props.currency}
            childId={mem.id}
            split={split}
          />);
          index++; 
        }

        if (age === undefined || age > 6) {
          steps.push(<StepOneCost 
            key={index}
            text={tn.lang.agreGuideTransport(mem.firstName)}
            next={() => this.step()}
            desc={tn.lang.agreGuideTransportDesc(mem.firstName.charAt(0))}
            repeat={EcoRepeat.MONTHLY}
            repeatEditable={true}
            currency={this.props.currency}
            childId={mem.id}
            split={split}
          />);
          index++; 
        }

        if (age === undefined || age > 5) {
          steps.push(<StepAddTitle 
            key={index}
            text={tn.lang.agreGuideActivity(mem.firstName)}
            textMultiple={tn.lang.agreGuideActivityMore(mem.firstName)}
            next={() => this.step()}
            repeat={EcoRepeat.TERM}
            repeatEditable={true}
            currency={this.props.currency}
            childId={mem.id}
            split={split}
          />);
          index++; 
        }

      }
    }

    steps.push(<StepSubs 
      key={index}
      next={() => this.step()}
      currency={this.props.currency}
      miltipleKids={(kids>1)}
      split={split}
    />); 
    index++;

    steps.push(<StepInfo 
      key={index}
      text={tn.lang.agreGuideDone}
      next={() => this.step()}
    />); 
    index++;

    return steps;
  }

  public step() {
    if (this.state.step < (this.state.steps.length - 1)) {
      this.setState({step:(this.state.step+1)});
    } else {
      this.props.onClose();
    }
    
  }

  public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (prevProps.family !== this.props.family) {
      this.setState({steps:this.createSteps(this.props)});
    }
  }

  public render() {
    const { isOpen, onClose } = this.props;


    return (
      <Dialog
        open={isOpen}
        onClose={() => onClose()}
      >
        <DialogTop >
          <Col style={{flex:1, alignItems: 'center'}}>
            <DialogTitle>{tn.lang.agreGuideTitle}</DialogTitle>
          </Col>
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        </DialogTop>
        {this.state.steps[this.state.step]}
      </Dialog>
    );
  }

}

function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;

  return {
    userId,
    family: state.family.family,
  };
}

const mapDispatchToProps = {
};


AgreementSetupDialog.contextType = CordovaContext;

export default connect(mapStateToProps,mapDispatchToProps)(withStyles(dialogStyles)(AgreementSetupDialog));



// WEBPACK FOOTER //
// ./src/components/economy/setup/AgreementSetupDialog.tsx